import {
  CircularGaugeChart, ellipsis, ExpandableContent, FontWeight, Grid, HStack, Icon, ICON_ACCOUNT_MULTIPLE_OUTLINE,
  Message, Stack, Text,
} from '@doveit/bricks';
import React from 'react';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import {
  ConversionMetric,
  IssueBelowTarget,
  WithIssues,
} from '../../../providers/api/dtos';
import { template } from '../../../utils/text/text';
import { getAgentName } from '../../utils/utils';
import ViewAgentSmallPreview from '../../containers/view-agent-small-preview/ViewAgentSmallPreview';
import { configByCrPerformanceRating } from './constants';

export interface ManagerConversionRateDetailViewProps extends React.AriaAttributes {
  cr: 'cr2' | 'cr3',
  metric: ConversionMetric & WithIssues<IssueBelowTarget>,
}

const ManagerConversionRateDetailView: React.FC<ManagerConversionRateDetailViewProps> = ({
  metric,
  cr,
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');

  const cfgByCrPerformanceRating = metric.status ? configByCrPerformanceRating[metric.status] : configByCrPerformanceRating.NOT_DEFINED;

  const agentsBelowTarget = (metric.issues || [])
    ?.filter(({ type }) => type === 'BELOW_TARGET')
    .map(({ agent, value }) => ({ agent, value }));

  return (
    <Stack gap={200} {...rest}>
      <HStack gap={300}>
        <CircularGaugeChart
          progress={metric.progress ?? 0}
          color={cfgByCrPerformanceRating.variant}
          size="L"
        />
        <Text.H4
          color={`${cfgByCrPerformanceRating.variant}.default.low`}
          css={ellipsis()}
        >
          {template(cfgByCrPerformanceRating.title, {
            CR: cr.toUpperCase(),
            CR_PERFORMANCE: metric.performance !== undefined ? (metric.performance * 100).toFixed() : 0,
          })}
        </Text.H4>
      </HStack>

      {agentsBelowTarget.length > 0 && (
        <HStack
          gap={300}
          wrap="nowrap"
          alignItems="start"
        >
          {!isMobile && (
            <div style={{ flexShrink: 0 }}>
              <Icon
                path={ICON_ACCOUNT_MULTIPLE_OUTLINE}
                size={40}
                color="neutral.default.highest"
              />
            </div>
          )}

          <Stack gap={100}>
            <Text.Body fontWeight={FontWeight.LIGHT}>
              I seguenti agenti sono sotto l&apos;obiettivo dichiarato:
            </Text.Body>

            <Grid gutter={50}>
              {agentsBelowTarget
                .slice(0, 6)
                .map(({ agent, value }) => (
                  <Grid.Unit key={agent.id} size={1 / Math.min(3, Math.ceil(agentsBelowTarget.length / 2))}>
                    <ViewAgentSmallPreview
                      name={getAgentName(agent)}
                      details={`${cr.toUpperCase()} al ${(value * 100).toFixed()}%`}
                      size="S"
                    />
                  </Grid.Unit>
                ))}
            </Grid>

            {agentsBelowTarget.length > 6 && (
              <ExpandableContent openLabel="Mostra tutti">
                <Grid gutter={50}>
                  {agentsBelowTarget
                    .slice(6)
                    .map(({ agent, value }) => (
                      <Grid.Unit key={agent.id} size={1 / 3}>
                        <ViewAgentSmallPreview
                          name={getAgentName(agent)}
                          details={`${cr.toUpperCase()} al ${(value * 100).toFixed()}%`}
                          size="S"
                        />
                      </Grid.Unit>
                    ))}
                </Grid>
              </ExpandableContent>
            )}
          </Stack>
        </HStack>
      )}

      {cfgByCrPerformanceRating.hint && (
        <Message
          type={cfgByCrPerformanceRating.variant}
          message={cfgByCrPerformanceRating.hint.title}
          icon={cfgByCrPerformanceRating.hint.icon}
        />
      )}
    </Stack>
  );
};

export default ManagerConversionRateDetailView;
